import React from "react";

import { useState } from "react";

import {
  MousePointer,
  Hand,
  Maximize2,
  Ruler,
  Triangle,
  Square,
  Type,
  MessageCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Separator } from "./ui/separator";
import { cn } from "../lib/utils";
import { dentalIcons } from "../../dentail-indetifcation/utils-icons/dentalIcons";
import { Slider } from "./ui/slider";
import useMediaQuery from "../../hooks/useMediaQuery";
import { InputNumber, Tooltip } from "antd";

export const BottomToolbar = ({
  activeTool,
  onToolChange,
  zoom,
  zoomViaSlider,
  onSetRotation,
  wrapperRotation,
  startScrub,
}) => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const MeasuremntButton = ({
    tool,
    isActive = false,
    disabled = false,
    onClick,
    icon,
    title,
  }) => {
    return (
      <div
        onClick={onClick || (() => onToolChange(tool.id))}
        onMouseEnter={() => setHoveredTool(tool.id)}
        onMouseLeave={() => setHoveredTool(null)}
        className={`flex items-center gap-1 ${
          isActive ? "bg-white" : ""
        }  rounded-[8px] px-2 py-2 h-full cursor-pointer`}
        style={{
          boxShadow:
            isActive &&
            "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
        }}
      >
        <img src={icon} alt="icon" className="h-4 w-4 min-w-[20px]" />
        <span>{title} </span>
        {/* Tooltip */}
        {hoveredTool === tool.id && (
          <div className="absolute top-[70px] mb-2 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="bg-gray-900 text-white text-xs font-medium px-3 py-2 rounded-md shadow-lg whitespace-nowrap">
              {tool.label}
              {tool.shortcut && (
                <span className="ml-2 opacity-75">{tool.shortcut}</span>
              )}
              {/* Tooltip arrow */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          </div>
        )}
      </div>
    );
  };
  const ToolButton = ({
    tool,
    isActive = false,
    disabled = false,
    onClick,
    icon,
  }) => {
    return (
      <div className="relative me-2">
        <div
          disabled={disabled}
          className={cn(
            "h-6 w-6 sm:h-7 sm:w-7 md:h-7 md:w-7 lg:h-8 lg:w-8 px-2.5 relative transition-all duration-200 ease-in-out",
            "flex items-center justify-center rounded-[12px]",
            "border border-transparent cursor-pointer",
            isActive
              ? "bg-[#20C3FF] text-white border-blue-600 shadow-sm hover:bg-[#20C3FF]"
              : "text-gray-600 hover:text-gray-800 hover:bg-[#20C3FF] hover:border-gray-200",
            disabled &&
              "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-gray-600",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:ring-opacity-50"
          )}
          onClick={onClick || (() => onToolChange(tool.id))}
          onMouseEnter={() => setHoveredTool(tool.id)}
          onMouseLeave={() => setHoveredTool(null)}
        >
          <img
            src={icon}
            className={cn(
              "w-4 h-4 min-w-[20px] transition",
              (isActive || hoveredTool === tool.id) && "icon-white"
            )}
          />
        </div>

        {/* Tooltip */}
        {hoveredTool === tool.id && (
          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="bg-gray-900 text-white text-xs font-medium px-3 py-2 rounded-md shadow-lg whitespace-nowrap">
              {tool.label}
              {tool.shortcut && (
                <span className="ml-2 opacity-75">{tool.shortcut}</span>
              )}
              {/* Tooltip arrow */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex justify-center  w-[100%]">
      <div className="bg-white border border-gray-200 rounded-xl px-2 py-1">
        {activeTool === "scale" && (
          <div className="absolute z-[9] left-[27%] flex items-center top-[60px] w-[100%] text-white p-1">
            <div
              className="relative overflow-visible w-[250px] flex items-center bg-white border border-[#E1E4EA] px-2 py-2.5 rounded-[12px]"
              style={{
                boxShadow:
                  "0 3px 2px 0 rgba(14, 18, 27, 0.04), 0 4px 12px 0 rgba(14, 18, 27, 0.06)",
              }}
            >
              <img
                src={dentalIcons.upToolTip}
                alt="arrow"
                className="absolute left-1/2 -translate-x-1/2 -top-2 z-[1001] w-4 h-4 pointer-events-none"
              />
              <Slider
                value={[zoom]}
                onValueChange={([v]) => zoomViaSlider(v)} // instant zoom
                min={1}
                max={2} // 200% cap for slider path
                step={0.01} // smooth control
                className="w-full"
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-1">
          {/* Primary Tools */}
          <div className="flex items-center gap-1">
            <ToolButton
              tool={{
                id: "scale",
                icon: Maximize2,
                label: "Zoom Image",
                shortcut: "Two-finger pinch to zoom Z",
              }}
              isActive={activeTool === "scale"}
              icon={dentalIcons.magnifier}
            />
            <Tooltip title="Rotate Image">
              <div
                className="h-8 lg:h-8 ps-1 relative transition-all duration-200 ease-in-out
            flex items-center justify-center rounded-[12px] border w-[90px] -ms-1 me-1"
              >
                <div className="rotate-handle" onMouseDown={startScrub}>
                  <img
                    src={dentalIcons.rotateAngle}
                    alt="angle"
                    className="h-10"
                    width={30}
                  />
                </div>
                <InputNumber
                  min={0}
                  controls={false}
                  bordered={false}
                  value={Number(wrapperRotation) ?? 0} // allow negatives
                  onChange={(v) => onSetRotation(Number(v ?? 0))}
                  formatter={(v) => `${v ?? 0}°`} // shows 12°
                  // parser={(v) => String(v ?? "").replace(/[^\d]/g, "")} // keeps it numeric
                  parser={(v) => String(v ?? "").replace(/[^\d-]/g, "")}
                  style={{ padding: "0px" }}
                  textAlign="right"
                />
              </div>
            </Tooltip>

            <ToolButton
              tool={{
                id: "pan",
                icon: Hand,
                label: "Move Image",
                shortcut: "H",
              }}
              isActive={activeTool === "pan"}
              icon={dentalIcons.handTool}
            />
          </div>

          <Separator orientation="vertical" className="h-6 mx-2" />

          {/* Measurement Tools */}
          <div className="flex items-center gap-1 bg-[#F5F7FA] px-1  py-1 rounded-[8px]">
            <MeasuremntButton
              tool={{
                id: "linear",
                icon: Ruler,
                label: "Linear measurement",
                shortcut: "L",
              }}
              isActive={activeTool === "linear"}
              icon={dentalIcons.leanerTool}
              title={isDesktop ? "Linear" : ""}
            />
            <MeasuremntButton
              tool={{
                id: "polygon",
                icon: Square,
                label: "Area measurement",
                shortcut: "P",
              }}
              isActive={activeTool === "polygon"}
              icon={dentalIcons.angleTool}
              title={isDesktop ? "Polygon" : ""}
            />
            <MeasuremntButton
              tool={{
                id: "angle",
                icon: Triangle,
                label: "Angle measurement",
                shortcut: "A",
              }}
              isActive={activeTool === "angle"}
              icon={dentalIcons.areaTool}
              title={isDesktop ? "Angle" : ""}
            />
          </div>

          <Separator orientation="vertical" className="h-6 mx-2" />

          {/* Annotation Tools */}
          <div className="flex items-center gap-1 -me-2">
            <ToolButton
              tool={{
                id: "open",
                icon: Type,
                label: "Edit Annotation",
              }}
              isActive={activeTool === "open"}
              icon={dentalIcons.openIcon}
            />

            <ToolButton
              tool={{
                id: "delete",
                icon: MessageCircle,
                label: "Delete Selection",
              }}
              isActive={activeTool === "delete"}
              icon={dentalIcons.clearAllIcon}
            />

            <ToolButton
              tool={{
                id: "select",
                icon: MousePointer,
                label: "Move Shape",
                shortcut: "V",
              }}
              isActive={activeTool === "select"}
              icon={dentalIcons.moveTool}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default BottomToolbar;
